import { createApi } from '@reduxjs/toolkit/query/react';
import { fakeBaseQuery } from '@reduxjs/toolkit/query';
import { sendApiRequestForTenant } from '../sendApiRequestForTenant';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import {
    MasterBooking,
    MasterDoor,
    MasterProblemReport,
    MasterStation,
    RawMasterBookingResponse,
    RawMasterDepotResponse,
    RawMasterDoorResponse,
    RawMasterLocationResponse,
    RawMasterProblemReportResponse,
    RawMasterThingResponse,
} from './masterApiTypes';

/**
 * Master Mode RTK Query slice.
 *
 * Every endpoint here is keyed by `tenant` and uses
 * `sendApiRequestForTenant` so concurrent per-tenant queries don't
 * collide on the singleton X-Tenant header. We use `queryFn` instead of
 * `query`/`axiosBaseQuery` because the base query reads tenant from
 * Redux — exactly the singleton we're trying to bypass.
 *
 * The booking endpoint hits the same `rental/bookings/...` URL as the
 * existing bookingApiSlice, but the response is defensively mapped to
 * the `MasterBooking` minimal shape — missing fields fall back to `''`
 * (strings) or `null` (dates), never throw.
 */

const isObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const stringOrEmpty = (value: unknown): string =>
    typeof value === 'string' ? value : '';

const stringOrNull = (value: unknown): string | null =>
    typeof value === 'string' && value.length > 0 ? value : null;

/**
 * Same use-case list the existing bookingApiSlice consumes — we send
 * every use case so the page shows whatever bookings each tenant has,
 * regardless of solution. The backend ignores unknown use cases.
 */
const ALL_USE_CASES = 'rental,storage,p2p,parcel,apartment,sup,venue';

const mapRawBooking = (raw: unknown, tenant: string): MasterBooking | null => {
    if (!isObject(raw)) return null;
    const candidate = raw as RawMasterBookingResponse;
    const id = stringOrEmpty(candidate.bookingId ?? candidate.id);
    if (!id) return null;
    return {
        id,
        tenantSlug: tenant,
        bookingNumber: stringOrEmpty(candidate.bookingNumber),
        startDate: stringOrNull(candidate.startDate),
        endDate: stringOrNull(candidate.endDate),
        finishedAtDate: stringOrNull(candidate.finishedAtDate),
        customerName: stringOrEmpty(candidate.senderFullname ?? candidate.customerName),
        status: stringOrEmpty(candidate.bookingStatus ?? candidate.status),
        useCase: stringOrEmpty(candidate.useCase),
        // Same signal the tenant-level BookingTable renders as its yes/no
        // issue badge: the list response attaches the booking's problem
        // reports inline.
        hasDamageReport:
            Array.isArray(candidate.damageReports) && candidate.damageReports.length > 0,
    };
};

const mapRawProblemReport = (
    raw: unknown,
    tenant: string,
): MasterProblemReport | null => {
    if (!isObject(raw)) return null;
    const candidate = raw as RawMasterProblemReportResponse;
    const id = stringOrEmpty(candidate.id);
    if (!id) return null;
    return {
        id,
        tenantSlug: tenant,
        bookingNumber: stringOrEmpty(candidate.bookingNumber),
        issueType: stringOrEmpty(candidate.issueType),
        state: stringOrEmpty(candidate.state),
        createdAt: stringOrNull(candidate.createdAt),
        userCreatorEmail: stringOrEmpty(candidate.userCreatorEmail),
    };
};

/**
 * Build a `locationId -> { name, bookable }` map from the raw location
 * list response so the station mapper can resolve both fields in O(1).
 * `bookable` is `null` when the location has no `status` block — we
 * surface that as "unknown" rather than guessing `false`.
 */
interface LocationLookupEntry {
    name: string;
    bookable: boolean | null;
}

const buildLocationLookup = (rows: unknown): Map<string, LocationLookupEntry> => {
    const map = new Map<string, LocationLookupEntry>();
    if (!Array.isArray(rows)) return map;
    rows.forEach((row) => {
        if (!isObject(row)) return;
        const candidate = row as RawMasterLocationResponse;
        const id = stringOrEmpty(candidate.id);
        if (!id) return;
        const name = stringOrEmpty(candidate.name);
        const bookable = typeof candidate.status?.bookable === 'boolean'
            ? candidate.status.bookable
            : null;
        map.set(id, { name, bookable });
    });
    return map;
};

/**
 * Build a `thingsId -> minutesFromLastSeen` map. Missing values stay
 * absent so the station mapper can render an "unknown" status pill
 * instead of assuming an offline value.
 */
const buildThingMinutesMap = (rows: unknown): Map<string, number> => {
    const map = new Map<string, number>();
    if (!Array.isArray(rows)) return map;
    rows.forEach((row) => {
        if (!isObject(row)) return;
        const candidate = row as RawMasterThingResponse;
        const id = stringOrEmpty(candidate.id);
        if (!id) return;
        if (typeof candidate.minutesFromLastSeen === 'number') {
            map.set(id, candidate.minutesFromLastSeen);
        }
    });
    return map;
};

/**
 * Same threshold the existing `DepotCard` uses: a thing is "online" if
 * it reported within the last 10 minutes, otherwise "offline". Anything
 * we cannot look up at all is "unknown".
 */
const deriveOnlineStatus = (
    minutes: number | undefined,
): MasterStation['onlineStatus'] => {
    if (minutes === undefined) return 'unknown';
    return minutes <= 10 ? 'online' : 'offline';
};

const mapRawStation = (
    raw: unknown,
    tenant: string,
    locationLookup: Map<string, LocationLookupEntry>,
    thingMinutes: Map<string, number>,
): MasterStation | null => {
    if (!isObject(raw)) return null;
    const candidate = raw as RawMasterDepotResponse;
    const id = stringOrEmpty(candidate.id);
    if (!id) return null;
    // Skip fictional depots — same filter the regular DepotsPage uses.
    if (candidate.isFictional) return null;
    const locationId = stringOrEmpty(candidate.locationId);
    const thingsId = stringOrEmpty(candidate.thingsId);
    const locationEntry = locationId ? locationLookup.get(locationId) : undefined;
    return {
        id,
        tenantSlug: tenant,
        name: stringOrEmpty(candidate.name),
        locationName: locationEntry?.name ?? '',
        isActive: candidate.enabled === true,
        isBookable: locationEntry ? locationEntry.bookable : null,
        onlineStatus: deriveOnlineStatus(thingMinutes.get(thingsId)),
        thingsId,
    };
};

const mapRawDoor = (raw: unknown): MasterDoor | null => {
    if (!isObject(raw)) return null;
    const candidate = raw as RawMasterDoorResponse;
    if (typeof candidate.door !== 'number') return null;
    const doorState: MasterDoor['doorState'] =
        candidate.doorState === 'open' ? 'open' : 'closed';
    const assetState: MasterDoor['assetState'] =
        candidate.assetState === 'blocked'
            || candidate.assetState === 'inUse'
            || candidate.assetState === 'soonAvailable'
            ? candidate.assetState
            : 'available';
    return {
        doorNumber: candidate.door,
        doorState,
        assetState,
    };
};

export const masterApiSlice = createApi({
    reducerPath: 'masterApiSlice',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: fakeBaseQuery(),
    // Mutations elsewhere (e.g. deleting a booking in the tenant context)
    // can't invalidate tags across slices, so cached master rows survived
    // a delete and kept showing removed bookings. Master pages are
    // monitoring views — always refetch when (re)entered.
    refetchOnMountOrArgChange: true,
    tagTypes: [
        'masterBookings',
        'masterProblemReports',
        'masterStations',
        'masterDoors',
    ],
    endpoints: (builder) => ({
        getMasterBookings: builder.query<MasterBooking[], { tenant: string }>({
            queryFn: async ({ tenant }) => {
                try {
                    const response = await sendApiRequestForTenant(tenant, {
                        method: 'GET',
                        url: `/rental/bookings/${ALL_USE_CASES}`,
                    });
                    const rows: unknown = response.data;
                    if (!Array.isArray(rows)) {
                        return { data: [] };
                    }
                    const mapped = rows
                        .map((row) => mapRawBooking(row, tenant))
                        .filter((row): row is MasterBooking => row !== null);
                    return { data: mapped };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR' as const,
                            data: error instanceof Error ? error.message : 'Unknown error',
                            error: error instanceof Error ? error.message : 'Unknown error',
                        },
                    };
                }
            },
            providesTags: (_result, _error, { tenant }) => [
                { type: 'masterBookings', id: tenant },
            ],
        }),
        getMasterProblemReports: builder.query<MasterProblemReport[], { tenant: string }>({
            queryFn: async ({ tenant }) => {
                try {
                    const response = await sendApiRequestForTenant(tenant, {
                        method: 'GET',
                        url: 'damage-reports',
                    });
                    const rows: unknown = response.data;
                    if (!Array.isArray(rows)) {
                        return { data: [] };
                    }
                    const mapped = rows
                        .map((row) => mapRawProblemReport(row, tenant))
                        .filter((row): row is MasterProblemReport => row !== null);
                    return { data: mapped };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR' as const,
                            data: error instanceof Error ? error.message : 'Unknown error',
                            error: error instanceof Error ? error.message : 'Unknown error',
                        },
                    };
                }
            },
            providesTags: (_result, _error, { tenant }) => [
                { type: 'masterProblemReports', id: tenant },
            ],
        }),
        /**
         * Cross-tenant station list for the lockers page. Fans out to
         * `depot`, `location`, and `things` in parallel, then joins on
         * `locationId` (for friendly name + bookable flag) and
         * `thingsId` (for online status). Tenants without locker use
         * cases return empty arrays and contribute zero rows.
         */
        getMasterStations: builder.query<MasterStation[], { tenant: string }>({
            queryFn: async ({ tenant }) => {
                try {
                    const [depotResponse, locationResponse, thingsResponse] =
                        await Promise.all([
                            sendApiRequestForTenant(tenant, {
                                method: 'GET',
                                url: 'depot?includeDisabled=true',
                            }),
                            sendApiRequestForTenant(tenant, {
                                method: 'GET',
                                url: 'location?includeDisabled=true',
                            }),
                            sendApiRequestForTenant(tenant, {
                                method: 'GET',
                                url: 'things',
                            }),
                        ]);
                    const locationLookup = buildLocationLookup(locationResponse.data);
                    const thingMinutes = buildThingMinutesMap(thingsResponse.data);
                    const rows: unknown = depotResponse.data;
                    if (!Array.isArray(rows)) {
                        return { data: [] };
                    }
                    const mapped = rows
                        .map((row) => mapRawStation(row, tenant, locationLookup, thingMinutes))
                        .filter((row): row is MasterStation => row !== null);
                    return { data: mapped };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR' as const,
                            data: error instanceof Error ? error.message : 'Unknown error',
                            error: error instanceof Error ? error.message : 'Unknown error',
                        },
                    };
                }
            },
            providesTags: (_result, _error, { tenant }) => [
                { type: 'masterStations', id: tenant },
            ],
        }),
        /**
         * Per-station doors list. Lazy-loaded by `MasterLockersPage`
         * only after a station row is expanded — keeps the initial
         * cross-tenant fan-out small. The backend returns rows under
         * `data.doorStatus`; we narrow each row defensively.
         */
        getMasterDoors: builder.query<
            MasterDoor[],
            { tenant: string, stationId: string }
        >({
            queryFn: async ({ tenant, stationId }) => {
                try {
                    const response = await sendApiRequestForTenant(tenant, {
                        method: 'GET',
                        url: `depot/${stationId}/doors`,
                    });
                    const payload: unknown = response.data;
                    const rows: unknown =
                        isObject(payload) ? payload.doorStatus : undefined;
                    if (!Array.isArray(rows)) {
                        return { data: [] };
                    }
                    const mapped = rows
                        .map((row) => mapRawDoor(row))
                        .filter((row): row is MasterDoor => row !== null)
                        .sort((a, b) => a.doorNumber - b.doorNumber);
                    return { data: mapped };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR' as const,
                            data: error instanceof Error ? error.message : 'Unknown error',
                            error: error instanceof Error ? error.message : 'Unknown error',
                        },
                    };
                }
            },
            providesTags: (_result, _error, { tenant, stationId }) => [
                { type: 'masterDoors', id: `${tenant}:${stationId}` },
            ],
        }),
    }),
});

export const {
    useGetMasterBookingsQuery,
    useGetMasterProblemReportsQuery,
    useGetMasterStationsQuery,
    useGetMasterDoorsQuery,
} = masterApiSlice;
